import type { PostedLearningRow, PostedTeachingRow } from "@/lib/api/classroomReferences";
import type { ClassroomSubmissionV6 } from "@/lib/api/courseHome";

export type ClassroomRecordTone = "act" | "news" | "none";

/**
 * A learner's own stored Classroom record, in the words every learner
 * surface uses for it: the Coursework rows and the course Home row agree.
 */
export function classroomLearnerState(submission: ClassroomSubmissionV6 | null): { text: string; tone: ClassroomRecordTone } {
  if (!submission) return { text: "No submission recorded", tone: "none" };
  switch (submission.state) {
    case "RETURNED": return { text: "Returned", tone: "news" };
    case "TURNED_IN": return { text: "Turned in", tone: "none" };
    case "RECLAIMED_BY_STUDENT": return { text: "Reclaimed", tone: "none" };
    default: return { text: "Not turned in", tone: "none" };
  }
}

/** Why Classroom history could not be read, in the server's words when it gave them. */
export function classroomReadFailure(projection: { classroom: { enabled: false } | { enabled: true; recovery: { reason: string } | null } }): string {
  return (projection.classroom.enabled && projection.classroom.recovery?.reason) || "Classroom records could not be read.";
}

/**
 * A Posted in Classroom row's last-read facts for the teaching team (board 6):
 * turned in n of m, then whether grades were returned, left as drafts never
 * released, or not given to work already past due.
 */
export function postedTeachingFact(row: Pick<PostedTeachingRow, "facts" | "due_at">, now: Date = new Date()): string {
  const { assigned, turned_in, returned_graded, draft_graded } = row.facts;
  const pastDue = row.due_at !== null && new Date(row.due_at) < now;
  const grading = returned_graded > 0 ? `, ${returned_graded} returned with grades`
    : draft_graded > 0 ? ", draft grades never released"
    : pastDue && turned_in > 0 ? ", none graded"
    : "";
  return `${turned_in} of ${assigned} turned in${grading} at last read`;
}

/** A learner's own last-read facts: only what Classroom returned to them. */
export function postedLearningFact(row: Pick<PostedLearningRow, "facts" | "points">): string | null {
  const { state, grade } = row.facts;
  // No fact of theirs at the last read (an unpaired learner, say) proves nothing: say nothing.
  if (state === "none") return null;
  if (state === "returned") {
    return grade === null ? "Returned at last read"
      : `Returned with ${grade}${row.points !== null ? ` of ${row.points}` : ""} at last read`;
  }
  return state === "turned_in" ? "Turned in at last read" : "Not turned in at last read";
}
